<template>
    <section class="table-scroller">
        <div class="appointments-header-container">
            <div>
                <h3 data-qa="appointments-heading">
                    Appointments
                </h3>
            </div>
            <div>
                <ds-filled-button data-qa="add-appointments-btn" @click="showAppointmentsModal">
                    Add an appointment
                </ds-filled-button>

                <AppointmentsForm ref="appointmentsModal" :contact="contact" :user-id="userId" />
            </div>
        </div>

        <div v-if="isContactHasAppointments" class="table-scroller" data-qa="appointments-table">
            <div v-if="isContactHasUpcomingAppointments" class="appointments-table">
                <div class="appointments-heading">
                    Upcoming appointments
                </div>

                <table class="upcoming-table">
                    <tr v-for="(appointment, index) in upcomingAppointments" :key="index" class="appointments-tr">
                        <td class="calendar-icon">
                            <ds-icon name="calendar" />
                        </td>

                        <td class="display-appointment" @click="openAppointment(appointment.id)">
                            <div class="appointments-details">
                                <div class="appointments-details">
                                    <span class="appointments-name">
                                        {{ appointment.title }}
                                    </span>

                                    <span class="display-user-name">Assigned to: {{ appointment.assignedUserName }}</span>
                                </div>
                            </div>
                        </td>
                        <td>
                            <div class="dateTime-appointment">
                                <span class="dateTime-span">{{ dateFormatDateStartDate(appointment.startDate, appointment.endDate, 'MM/DD/YYYY') }}</span>

                                <span class="dateTime-span">{{ dateFormatDateEndDate(appointment.startDate, appointment.endDate, 'MM/DD/YYYY') }}</span>
                            </div>
                        </td>
                    </tr>
                </table>
            </div>

            <div class="past-appointments">
                <div v-if="isContactHasPastAppointments" class="appointments-heading">
                    Past appointments
                </div>

                <table class="past-table">
                    <tr v-for="(appointment, indexes) in previousAppointments" :key="indexes" class="appointments-tr">
                        <td class="calendar-icon">
                            <ds-icon name="calendar" />
                        </td>

                        <td class="display-appointment" @click="openAppointment(appointment.id)">
                            <div class="appointments-details">
                                <div class="appointments-details">
                                    <span
                                        class="appointments-name"
                                    >
                                        {{ appointment.title }}
                                    </span>

                                    <span class="display-user-name">Assigned to: {{ appointment.assignedUserName }}</span>
                                </div>
                            </div>
                        </td>

                        <td>
                            <div class="dateTime-div">
                                <span class="past-dateTime-span">{{ dateFormatDateStartDate(appointment.startDate,appointment.endDate, 'MM/DD/YYYY') }}</span>

                                <span class="past-dateTime-span">{{ dateFormatDateEndDate(appointment.startDate,appointment.endDate, 'MM/DD/YYYY') }}</span>
                            </div>
                        </td>
                    </tr>
                </table>
            </div>
        </div>

        <div v-if="!isContactHasAppointments" class="no-appointments-section">
            <p class="no-appointments-label">
                No appointments!
            </p>
        </div>
    </section>
</template>

<script>
import axios from 'axios';
import moment from 'moment-timezone';
import AppointmentsForm from './AppointmentsForm';

export default {
    components: {
        AppointmentsForm,
    },

    props: {
        contact: Object,
        userId: [String, Number],
    },

    data() {
        return {
            appointments: [],
            previousAppointments: [],
            upcomingAppointments: [],
            contactId: this.contact.id,
            loaded: false,
            MAX_APPOINTMENTS_LIMIT: 25,
            timeZoneId: '',
        };
    },

    created() {
        if (!this.loaded) {
            this.initTab();
            this.loaded = true;
        }
    },

    mounted() {
        this.$root.$on('APPOINTMENTS_STATUS', (isSuccess) => {
            if (isSuccess) {
                this.initTab();
            }
        });
    },

    beforeDestroy() {
        this.$root.$off('APPOINTMENTS_STATUS');
    },

    computed: {
        isContactHasAppointments() {
            return this.previousAppointments?.length > 0 || this.upcomingAppointments?.length > 0;
        },

        isContactHasPastAppointments() {
            return this.previousAppointments?.length > 0;
        },

        isContactHasUpcomingAppointments() {
            return this.upcomingAppointments?.length > 0;
        },
    },

    methods: {
        initTab() {
            this.loadContactAppointments();
            this.getAppTimeZones();
        },

        async loadContactAppointments() {
            try {
                const response = await axios.get(`/app/appointments/${this.contactId}/${this.MAX_APPOINTMENTS_LIMIT}`);

                this.previousAppointments = response.data.previousAppointments;
                this.upcomingAppointments = response.data.upcomingAppointments;
                this.appointments = response.data;
            } catch (error) {
                this.$error({ message: 'unable to get all appointments' });
            }
        },

        async getAppTimeZones() {
            try {
                const response = await axios.get('/app/appointments/getTimeZones');

                this.timeZoneId = response.data.userTimeZone;
            } catch (error) {
                this.$error({ message: 'unable to get user Time Zone' });
            }
        },

        dateFormatDateStartDate(startDate, endDate, format = 'llll') {
            const formattedDate = moment(startDate).tz(this.timeZoneId)?.isValid() ? moment(startDate).tz(this.timeZoneId) : moment().tz(this.timeZoneId);
            const formattedStartDate = moment(startDate).tz(this.timeZoneId).format(format);
            const formattedEndDate = moment(endDate).tz(this.timeZoneId).format(format);
            const today = moment(new Date()).tz(this.timeZoneId).format(format);
            const tomorrow = moment(new Date()).tz(this.timeZoneId).add(1, 'days').format(format);
            const yesterday = moment(new Date()).tz(this.timeZoneId).add(-1, 'days').format(format);

            if (formattedStartDate === formattedEndDate) {
                if (formattedStartDate === today) {
                    return 'Today';
                }

                if (formattedStartDate === tomorrow) {
                    return 'Tomorrow';
                }

                if (formattedStartDate === yesterday) {
                    return 'Yesterday';
                }

                return moment(startDate).tz(this.timeZoneId).format('llll').substr(0, 17);
            }

            if (formattedStartDate !== formattedEndDate) {
                if (formattedStartDate === today) {
                    return 'Today '.concat(moment(startDate).tz(this.timeZoneId).format('hh:mm A'));
                }

                if (formattedStartDate === tomorrow) {
                    return 'Tomorrow '.concat(moment(startDate).tz(this.timeZoneId).format('hh:mm A'));
                }

                if (formattedStartDate === yesterday) {
                    return 'Yesterday '.concat(moment(startDate).tz(this.timeZoneId).format('hh:mm A'));
                }

                return moment(startDate).tz(this.timeZoneId).format('llll');
            }

            return moment(formattedDate).format(format);
        },

        dateFormatDateEndDate(startDate, endDate, format = 'll') {
            const formattedDate = moment(startDate).tz(this.timeZoneId).isValid() ? startDate : moment(new Date(startDate)).tz(this.timeZoneId);
            const formattedStartDate = moment(startDate).tz(this.timeZoneId).format(format);
            const formattedEndDate = moment(endDate).tz(this.timeZoneId).format(format);
            const today = moment().tz(this.timeZoneId).format(format);
            const tomorrow = moment().tz(this.timeZoneId).add(1, 'days').format(format);
            const yesterday = moment().tz(this.timeZoneId).add(-1, 'days').format(format);
            const startTime = moment(startDate).tz(this.timeZoneId).format('hh:mm A');
            const endTime = moment(endDate).tz(this.timeZoneId).format('hh:mm A');

            if (formattedStartDate === formattedEndDate && startTime === endTime) {
                return '12:00 AM - 11:59 PM';
            }

            if (formattedStartDate === formattedEndDate) {
                return startTime.concat(' - ').concat(endTime);
            }

            if (formattedStartDate !== formattedEndDate) {
                if (formattedEndDate === today) {
                    return 'Today '.concat(moment(endDate).tz(this.timeZoneId).format('hh:mm A'));
                }

                if (formattedEndDate === tomorrow) {
                    return 'Tomorrow '.concat(moment(endDate).tz(this.timeZoneId).format('hh:mm A'));
                }

                if (formattedEndDate === yesterday) {
                    return 'Yesterday '.concat(moment(endDate).tz(this.timeZoneId).format('hh:mm A'));
                }

                return moment(endDate).tz(this.timeZoneId).format('llll');
            }

            return moment(formattedDate).tz(this.timeZoneId).format(format);
        },

        openAppointment(id) {
            this.appointmentsId = id;
            this.$refs.appointmentsModal.showModal(id);
        },

        showAppointmentsModal() {
            this.$refs.appointmentsModal.showModal(0);
        },
    },

};
</script>


<style lang="scss" scoped>
.appointments-header-container {
    display: flex;
    justify-content: space-between;
    margin-top: $spacing-250;
}

.appointments-heading {
    margin-top: $spacing-400;
    font-size: $font-size-md;
}

.appointments-details {
    display: flex;
    flex-direction: column;
    cursor: pointer;
}

.table-scroller {
    white-space: nowrap;
    overflow-x: auto !important;
}

.appointments-table {
    border-collapse: collapse;
    border-top: 1px solid $color-gray-lighter;
    width: 100%;
    margin-top: $spacing-250;
}

.past-appointments {
    border-collapse: collapse;
    border-top: 1px solid $color-gray-lighter;
    width: 100%;
    margin-top: $spacing-250;
}

.appointments-details {
    display: flex;
    justify-content: space-between;
    align-content: center;
    margin-top: auto;
    margin-bottom: auto;
    cursor: pointer;
    overflow-x:auto;
}

.appointments-name {
    font-size: $font-size-md;
    line-height: $line-height-lg;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.appointments-tr:hover {
    background-color: $color-gray-20;
}

.dateTime-div {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    font-size: $font-size-xs;
    width: 20%;
}

.dateTime-span {
    text-align: right;
}

.past-dateTime-span {
    text-align: right;
    color: $color-gray-darker;
}

.appointments-title {
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.appointments-assigned {
    margin: 0;
    line-height: 1.67;
}

.appointments-body {
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.no-appointments-section {
    width: 100%;
    text-align: center;
    border-top: 1px solid $color-gray-lighter;
    margin-top: $spacing-200;
}

.no-appointments-label {
    color: $color-gray-darker;
    font-size: $font-size-lg;
    font-weight: 300;
    padding-top: $spacing-800;
    padding-bottom: $spacing-600;
}

.display-user-name {
    height: 1rem;
    color: $color-gray-darker;
    font-size: $font-size-xs;
}

.upcoming-table > tr:last-child {
    border-bottom: 0;
}

.past-table > tr:last-child {
    border-bottom: 0;
}

.calendar-icon {
    width: 5%
}

.display-appointment {
    width: 75%;
}

.dateTime-appointment {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    font-size: $font-size-sm;
}
</style>
